import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting.
 *
 * This function uses DOMPurify to strip out dangerous elements like scripts,
 * event handlers, and other potential XSS vectors while allowing safe HTML
 * tags for content formatting.
 *
 * @param html - The raw HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      // Text formatting
      'p', 'br', 'span', 'div', 'strong', 'b', 'em', 'i', 'u', 's', 'mark', 'small', 'sub', 'sup',
      // Headings
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      // Lists
      'ul', 'ol', 'li',
      // Links and media
      'a', 'img',
      // Block elements
      'blockquote', 'pre', 'code',
      // Tables
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      // Horizontal rule
      'hr',
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'title',
      'src', 'alt', 'width', 'height',
      'class', 'id',
      'colspan', 'rowspan',
    ],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit'],
    SANITIZE_DOM: true,
    KEEP_CONTENT: true,
    IN_PLACE: false,
  });
}

/**
 * Creates a sanitized HTML object safe for use with dangerouslySetInnerHTML.
 *
 * @param html - The raw HTML string to sanitize
 * @returns Object with __html property containing sanitized HTML
 */
export function createSafeHtml(html: string): { __html: string } {
  return { __html: sanitizeHtml(html) };
}

/**
 * Sanitizes plain text input by removing any HTML tags and limiting length.
 *
 * @param input - The input string to sanitize
 * @param maxLength - Maximum length of the string (default: 5000)
 * @returns Sanitized plain text string
 */
export function sanitizeInput(input: string, maxLength: number = 5000): string {
  if (!input) return '';

  const stripped = input.replace(/<[^>]*>/g, '');

  return stripped.substring(0, maxLength);
}
